import { WalletError } from '@cef-ai/wallet-api-client';
import { walletErrorMessage } from '~/wallet';

/**
 * Which of the three registration steps produced the failure.
 *
 * Shared by BOTH registration surfaces: the standalone `/register` route and
 * the embedded prompt the register popup shows a host (Task 22). They render
 * the same steps and offer the same actions, so they must say the same things
 * about the same codes.
 */
export type RegisterStep = 'email' | 'otp' | 'passkey';

/**
 * Registration-specific copy, branched on `WalletError.code` — the tail of the
 * problem URN the server sent — and NEVER on a message string. Two reasons
 * that matters here rather than being a style preference:
 *
 * - `rate-limited` and `otp-invalid` are different situations with different
 *   remedies (wait vs. re-read the code), and the only stable thing that tells
 *   them apart is the code. Detail strings are server copy: they change, they
 *   localise, and they are absent on a transport failure.
 * - The same code means different things at different steps: `rate-limited` on
 *   `email/start` is "too many codes sent to this address", on `email/verify`
 *   it is "too many attempts at this code". The step disambiguates; the code
 *   still selects.
 *
 * Anything not named here falls through to the app-wide `walletErrorMessage`,
 * so a code this flow has no special copy for still reads as itself.
 */
export function registerErrorMessage(e: unknown, step: RegisterStep): string {
  if (e instanceof WalletError) {
    switch (e.code) {
      case 'rate-limited':
        return step === 'email'
          ? 'Too many codes have been requested for that address. Wait a few minutes, then try again.'
          : 'Too many attempts. Wait a few minutes, then request a new code.';
      case 'email-unverified':
        // Reached when a step AFTER `email/verify` rejects the token — most
        // plausibly because `passkey/register/start` already spent it (it is
        // single-use) and the ceremony then failed. `Register` returns the user
        // to the address step on this code, so the copy has to tell them why
        // they are back there and what to do next.
        return 'That confirmation code has already been used. Enter your address again and we will send a new one.';
      case 'otp-invalid':
        return "That code didn't match. Check the six digits, or request a new code.";
      case 'email-conflict':
        // Reached only if the server rejects the token at register/start after
        // `email/verify` already said `verified` — a race between two
        // registrations of the same address, not a UI state we can prevent.
        return 'That email address is already in use. Sign in instead.';
      case 'validation':
        return step === 'email'
          ? "That doesn't look like an email address. Check it and try again."
          : 'That code looks incomplete — it is six digits.';
      default:
        return walletErrorMessage(e);
    }
  }
  return walletErrorMessage(e);
}
